import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { FOLDER_LIMIT } from '@flow/libs/oneflow-client/folders';
import { useFolderQueryContext, useSyncQueryName } from '@app/contexts/folder-query-context';
import foldersReducer from '@app/reducers/entities/folders';
import useAsRef from '@app/hooks/use-as-ref';
import useClientFeatureFlag from '@app/hooks/use-client-feature-flag';
import { getFoldersQueryName } from '@app/components/folder-tree/helper';

function hashQueryKey(queryKey: (string | number | boolean)[]) {
  return JSON.stringify(queryKey);
}

type QueryParams = {
  workspaceId: Oneflow.Workspace['id'];
  parentId: Oneflow.Folder['id'];
  includeFolderSubtree: 0;
  includeChildCount: 1;
};

type Props = {
  params?: QueryParams;
  queryKey: (string | number | boolean)[];
  enabled?: boolean;
};

/**
 * Hook that provides the state of a folders query by its name.
 *
 * @param queryName - The unique name identifier for the query
 * @returns Object containing query state information:
 *   - loading: boolean indicating if the query is currently loading
 *   - error: boolean indicating if the query encountered an error
 *   - pristine: boolean indicating if the query hasn't been executed yet
 *   - success: boolean indicating if the query completed successfully
 *   - count: number of total folders available
 *   - data: array of folder objects from the query result
 */
export function useFoldersQueryState(queryName: string) {
  const foldersQuery = useSelector(
    (state) =>
      foldersReducer.getQuerySelector(state, {
        name: queryName,
      }),
    shallowEqual,
  );
  const folders = useSelector(
    (state) =>
      foldersReducer.getFoldersSelector(state, {
        ids: foldersQuery.result,
      }),
    shallowEqual,
  );

  return {
    loading: foldersQuery.status === 'loading',
    error: foldersQuery.status === 'error',
    pristine: foldersQuery.status === 'pristine',
    success: foldersQuery.status === 'success',
    count: foldersQuery.count,
    data: folders,
  };
}

/**
 * A reactive hook for managing folder queries with pagination.
 * The hook is reactive to the queryKey array and will re-run the query when the queryKey array changes.
 *
 * @param props - Configuration object
 * @param props.params - Query parameters including workspaceId, parentId, etc.
 * @param props.queryKey - Array of values used to generate a unique query identifier. The query will be re-run when the queryKey array changes.
 * @param props.enabled - Whether the query should be enabled - if false, the query will not be executed
 * @returns Object containing query methods and state:
 *   - fetch: function to trigger a new query
 *   - loadMore: function to load additional results with pagination
 *   - data: array of folder objects from the query result
 *   - loading: boolean indicating if the query is currently loading
 *   - error: boolean indicating if the query encountered an error
 *   - pristine: boolean indicating if the query hasn't been executed yet
 *   - success: boolean indicating if the query completed successfully
 *   - count: number of total folders available
 */
export function useFoldersQuery({ params, queryKey, enabled = true }: Props) {
  const dispatch = useDispatch();
  // biome-ignore lint/correctness/useExhaustiveDependencies: the queryName should only rely on queryKey array
  const queryName = useMemo(() => hashQueryKey(queryKey), queryKey);
  // Store the params in a ref to have access to the latest value without re-rendering the hooks where the params are used
  const paramsRef = useAsRef(params);

  const { loading, error, pristine, success, count, data } = useFoldersQueryState(queryName);
  useSyncQueryName(queryName);

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need to include paramsRef.current in the dependencies as it is a ref
  const fetch = useCallback(() => {
    dispatch(
      foldersReducer.queryFolders({
        name: queryName,
        pagination: {
          offset: 0,
          limit: FOLDER_LIMIT,
        },
        params: paramsRef.current,
        sort: [
          'active',
        ],
      }),
    );
  }, [
    dispatch,
    queryName,
  ]);

  const loadMore = useCallback(
    (additionalResults: number) => {
      if (loading || count <= data.length) {
        return;
      }

      dispatch(
        foldersReducer.queryFoldersLoadMore({
          name: queryName,
          additionalResults,
        }),
      );
    },
    [
      dispatch,
      queryName,
      loading,
      count,
      data.length,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: the hook is reactive to the queryName
  useEffect(() => {
    if (!enabled || !paramsRef.current) {
      return;
    }
    fetch();
  }, [
    queryName,
    enabled,
    fetch,
  ]);

  return {
    fetch,
    loadMore,
    data,
    loading,
    error,
    pristine,
    success,
    count,
  };
}

/**
 * Hook that provides a reload function for folders queries.
 * Handles both legacy and folder-v2 versions based on feature flag.
 *
 * @param isModalVersion - Whether this is for the modal version (defaults to false)
 * @returns the function to reload the folders query
 *
 * @todo Remove isModalVersion parameter when folder-v2 is released
 */
export const useFoldersQueryReload = (isModalVersion = false) => {
  const dispatch = useDispatch();
  const isFolderV2: boolean = useClientFeatureFlag('folder-v2');
  const context = useFolderQueryContext();
  const queryName = context?.queryName ?? getFoldersQueryName(isModalVersion);

  const reload = useCallback(() => {
    dispatch(
      foldersReducer.queryFoldersReload({
        name: isFolderV2 ? queryName : getFoldersQueryName(isModalVersion),
      }),
    );
  }, [
    dispatch,
    queryName,
    isFolderV2,
    isModalVersion,
  ]);

  return reload;
};
